/**
 * SPR — Spelling Regels rulebook.
 *
 * Sourced from DEFAULT_POLICY (SpellingRegel entries) +
 * docs/audits/DIDACTISCH_AUDIT_SPR_2026.md + MODULE_AUTHORING_GUIDE §6.
 *
 * v2 (mei 2026, sessie 2) — uitgebreid n.a.v.
 * docs/archive/VISUALS_QUESTIONTYPES_RULEBOOK_AUDIT_2026.md (P1-1):
 *   - customTypeRoles toegevoegd voor categorize / error_spotting /
 *     drag_drop_fill / text_highlight, zodat de pedagogy-stage DOK-alignment
 *     voor deze types weet binnen SPR.
 *   - discouragedTypes: true_false_series (recognition-only, geeft geen
 *     spellingsregel-toepassing).
 *   - familyVisualHints uitgebreid met meer nuance per klank-/weet-/leen-/
 *     sam-familie, en met DOK-niveau-onderbouwing in de notes.
 *   - preferredAidTypes uitgebreid met kofschipMnemonic (hoewel die primair
 *     in SWW leeft, hier expliciet niet preferred — vermijdt cross-module
 *     drift).
 *
 * v3 (14 juni 2026) — synchroniseerde de rulebook met de SPR-productietype-
 * ronde (zien-is-doen / dictatish):
 *   - De nieuwe SPR-specialist productietypen `spelling_pick` (letterkeuze-
 *     cloze) en `word_builder` (volledige-woord-producent, dictee-waardig)
 *     zijn nu first-class: opgenomen in preferredTypes + customTypeRoles
 *     (DOK 2, productie). Ze waren al gewired (registry, dataSchemas,
 *     diversityStage PRODUCTION_TYPES, questionTypeDefinitions) en in gebruik
 *     in SPR-KLANK-AU-OU / SPR-WEET-D-T / SPR-WEET-EI-IJ / SPR-WEET-DT,
 *     maar ontbraken in de didactische rulebook.
 *   - familyTypeHints voor ^SPR-WEET- zetten productie (spelling_pick /
 *     word_builder / fill_in) vóór herkenning en markeren `multiple_choice`
 *     nu als discouraged op recall-families — consistent met de shadow-check
 *     CONTENT_SPR_WORD_VISIBLE (sprVisibleWordChecks.ts), die recall-MC met
 *     volledig-gespelde opties als verklap-vraag flagt.
 *   - keyRules wijzen auteurs op de twee SPR-shadow-checks
 *     (CONTENT_SPR_WORD_VISIBLE + CONTENT_RULE_ENUMERATION_IN_STEM).
 *
 * v4 (14 juni 2026) — precisie-fix op de klank-familyVisualHints:
 *   - De gecombineerde hint `^SPR-KLANK-(VERDUBBEL|VERKORTEN)` beschreef in
 *     z'n note alléén het VERDUBBEL-fenomeen (medeklinkerverdubbeling in een
 *     gesloten lettergreep, "man → mannen") en zweeg over / mislabelde
 *     VERKORTEN, dat juist het tegenovergestelde is (open lettergreep,
 *     verenkeling: maan → manen). Gesplitst in twee aparte hints met elk een
 *     correcte, fenomeen-specifieke note. `preferredSupport: 'syllableBlocks'`
 *     blijft voor beide ongewijzigd — de seeds bevestigen dat dit klopt
 *     (VERDUBBEL toont gesloten lettergrepen, VERKORTEN open lettergrepen).
 *     Geen roleScope toegevoegd en geen seed gewijzigd: de bredere
 *     syllableBlocks-vs-rule-afweging blijft bewust open ("bij twijfel niet
 *     doen").
 *
 * v5 (25 juni 2026) — CITO-context familyTypeHint gesplitst (overreach-fix):
 *   - De gecombineerde hint `^SPR-(TREMA|HOOFDLETTER|ACCENT)` ontmoedigde
 *     categorize + matching + true_false_series voor alle drie de families.
 *     Code-validatie tegen de live seeds toont dat dit te breed was:
 *     SPR-TREMA-MV-008 gebruikt categorize ("trema nodig / geen trema nodig")
 *     en SPR-ACCENT-006 gebruikt matching ("woord → accenttype") — beide zijn
 *     didactisch geldige, goed opgebouwde classificatie-items op hoog tier
 *     (9 resp. 13), geen abstracte recall-vragen. De brede hint flagde ze
 *     onterecht als PEDAGOGY_FAMILY_TYPE_DISCOURAGED.
 *   - Gesplitst in: een strikte `^SPR-HOOFDLETTER`-hint (CITO-norm: alleen
 *     zinscontext; T-F/matching/categorize verboden — die familie bestaat uit
 *     losse woord-categorisatie zonder context) en een soepelere
 *     `^SPR-(TREMA|ACCENT)`-hint die productie/fout-spotten als voorkeur zet
 *     maar categorize/matching NIET ontmoedigt (alleen T-F-only blijft zwak).
 */

import type { ModuleRulebook } from './types'

export const SPR_RULEBOOK: ModuleRulebook = {
  moduleId: 'SpellingRegel',
  shortCode: 'SPR',
  displayName: 'Spelling Regels',

  didactics: {
    principles: [
      'open_gesloten_lettergreep',
      'klank_versus_schrijf',
      'leenwoorden',
      'samenstellingen',
      'regel_toepassen_niet_herkennen',
    ],
    schoolbookReferences: ['Staal Spelling', 'Taal Actief'],
    misconceptionDomain: 'spr',
    coreSummary:
      'Spellingregels: open/gesloten lettergrepen, verdubbelen/verkorten, trema, leenwoorden, samenstellingen. Toepassing op nieuw woord > herkennen van bekend woord.',
  },

  notation: {},

  content: {
    // GSR-26 (edtech-review 28 juni): de pattern dekte alleen de KLANK-/
    // samenstelling-machinerie (lettergreep/trema/tussen-n/verdubbelen). De
    // "weet-regel"-families (SPR-WEET-D-T, -EI-IJ, SPR-HOOFDLETTER, SPR-ACCENT)
    // zijn pure memoriseer-regels zónder lettergreep-analyse — hun didactische
    // kern is de weet-regel zelf (meervoud-check bij d/t, "uit je hoofd leren"
    // bij ei/ij, eigennaam/zinsbegin bij hoofdletter, accent-soort bij accent).
    // Die families leveren de regel bovendien meestal via een rule-card (door de
    // structural-escape in explanationChecks al afgedekt), maar de vocabulaire
    // hieronder vangt ook de prozatekst-varianten. Pure uitbreiding.
    distractorMisconceptionPattern:
      'open lettergreep|gesloten lettergreep|trema|koppelteken|tussen-n|samengesteld|leenwoord|verdubbelen|verkorten|weetwoord|uit je hoofd|meervoud|hoofdletter|eigennaam|zinsbegin|accent',
  },

  explanation: {
    requireRuleReference: true,
    checkStrategyMinTier: 9,
    preferredStrategyKeywords: [
      'open lettergreep',
      'gesloten lettergreep',
      'verdubbelen',
      'verkorten',
      'trema',
      'meervoud',
      'leenwoord',
    ],
  },

  visuals: {
    preferredAidTypes: ['syllableBlocks', 'rule'],
    discouragedAidTypes: [
      // kofschipMnemonic is een SWW-werkwoord-visual (d/t in verleden tijd);
      // gebruik hier verstoort de scheiding SPR (woordbeeld) vs SWW
      // (werkwoordsvervoeging). Past niet bij SPR-WEET-D-T (zelfstandig
      // naamwoord-spelling).
      'kofschipMnemonic',
    ],
    familyVisualHints: [
      // — Klankregels: lettergreep-analyse is de kern —
      {
        familyIdPattern: '^SPR-KLANK-(AA-EE-OO|AU-OU)',
        preferredSupport: 'syllableBlocks',
        note: 'Klinker-onderscheid: lettergrepen splitsen toont waarom open lettergreep lange klank krijgt.',
      },
      {
        familyIdPattern: '^SPR-KLANK-VERDUBBEL',
        preferredSupport: 'syllableBlocks',
        note: 'Medeklinkerverdubbeling: visueel zien dat een korte klank in een GESLOTEN lettergreep de medeklinker verdubbelt (kat → kat·ten, kop → kop·pen).',
      },
      {
        familyIdPattern: '^SPR-KLANK-VERKORTEN',
        preferredSupport: 'syllableBlocks',
        note: 'Open lettergreep (verenkeling): visueel zien dat een lange klank in een OPEN lettergreep met één klinker geschreven wordt (maan → ma·nen, boom → bo·men). Tegenovergestelde fenomeen van VERDUBBEL.',
      },
      {
        familyIdPattern: '^SPR-KLANK-(JAGER|BAKKER)',
        preferredSupport: 'syllableBlocks',
        note: 'Open/gesloten lettergreep-trick: ja-ger (open, lange a) vs bak-ker (gesloten, korte a).',
      },
      {
        familyIdPattern: '^SPR-KLANK-',
        preferredSupport: 'syllableBlocks',
        autoExemptEmbedded: true,
        note: 'Default voor klankregels: lettergrepen visueel splitsen helpt klankregel-besluit.',
      },

      // — Weet-regels: regel-kaart met expliciete uitzonderingen —
      {
        familyIdPattern: '^SPR-WEET-(D-T|DT)',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'D/t-keuze in zelfstandig naamwoorden: regel-kaart met meervoud-check (hond → honden → hond met d).',
      },
      {
        familyIdPattern: '^SPR-WEET-EI-IJ',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Ei/ij: pure weet-regel, geen klankonderscheid → regel-kaart met meest voorkomende families en uitzonderingen.',
      },
      {
        familyIdPattern: '^SPR-WEET-',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Default voor weet-regels: regel-kaart als geheugensteun.',
      },

      // — Trema: regel-kaart, want voornamelijk weet-regel —
      {
        familyIdPattern: '^SPR-TREMA',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Trema voorkomt verkeerde klinker-samenvoeging (zee-en → zeeën); regel-kaart met meervoudspatroon.',
      },

      // — Leenwoorden: per-bron-regel met taalspecifieke patronen —
      {
        familyIdPattern: '^SPR-LEEN-ENGELS',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Engelse leenwoorden: spelling vaak Engels behouden (manager, computer); regel-kaart per categorie (vakwoorden, internet).',
      },
      {
        familyIdPattern: '^SPR-LEEN-FRANS',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Franse leenwoorden: ai/au/eau/é-spelling; regel-kaart met fonologische voorbeelden (cadeau, bureau, café).',
      },
      {
        familyIdPattern: '^SPR-LEEN-GRIEKS',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Griekse leenwoorden: ch/th/ph en y-spelling (chronisch, theater, fysica); regel-kaart per fonologische groep.',
      },
      {
        familyIdPattern: '^SPR-LEEN-LATIJN',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Latijnse leenwoorden: -tie/-isch/-um-eindes; regel-kaart per achtervoegsel.',
      },
      {
        familyIdPattern: '^SPR-LEEN-C-S-K',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'C/s/k-onderscheid in leenwoorden: regel-kaart met klank-naar-letter-mapping (cel/sel, café/kafé).',
      },

      // — Samenstellingen (tussen-n / tussen-s) —
      {
        familyIdPattern: '^SPR-SAM-TUSSEN-N',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Tussen-n: meervoud-uitsluitings-regel (boekenkast: meervoud "boeken" → tussen-n). Regel-kaart met beheers-toets.',
      },
      {
        familyIdPattern: '^SPR-SAM-TUSSEN-S',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Tussen-s: klank-test (stadhuis → tussen-s hoor je); regel-kaart met klanktoets.',
      },

      // — Leestekens, hoofdletters, accenten —
      {
        familyIdPattern: '^SPR-LEESTEKEN-',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Leestekens (punt/komma/vraag/uitroep): regel-kaart met zinsfunctie per teken. CITO-norm: leestekens ALTIJD in zinscontext toetsen, nooit als abstracte regel-kennis.',
      },
      {
        familyIdPattern: '^SPR-HOOFDLETTER',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Hoofdletters: regel-kaart per categorie (zinsbegin, eigennaam, namen van talen/dagen).',
      },
      {
        familyIdPattern: '^SPR-ACCENT',
        preferredSupport: 'rule',
        roleScope: 'rule-only',
        note: 'Accenttekens (é, ë, è): regel-kaart met beklemtoning- en herkomst-regel.',
      },
    ],
  },

  questionTypes: {
    preferredTypes: [
      // Productie-first: het kind moet de spelling PRODUCEREN, niet herkennen.
      'spelling_pick',
      'word_builder',
      'fill_in',
      'categorize',
      'error_spotting',
      'drag_drop_fill',
      'text_highlight',
      // multiple_choice blijft valide voor herkennings-legitieme families
      // (leen/sam bron-taal), maar staat bewust ACHTERAAN — op recall-families
      // wordt het door familyTypeHints + CONTENT_SPR_WORD_VISIBLE ontmoedigd.
      'multiple_choice',
    ],
    discouragedTypes: [
      {
        type: 'true_false_series',
        reason:
          'Recognition-only — kind hoeft de regel niet toe te passen, alleen "goed/fout" oordelen. Voor SPR is regel-toepassing op nieuw woord essentieel (DOK 2-3), niet herkenning (DOK 1).',
      },
    ],
    customTypeRoles: [
      {
        type: 'categorize',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 5, max: 16 },
        reason:
          'Woorden indelen in open/gesloten-lettergreep of klank-categorie dwingt regel-toepassing — sterk didactisch alternatief voor MC.',
      },
      {
        type: 'error_spotting',
        didacticRole: 'strategic',
        preferredDok: 3,
        appropriateTiers: { min: 7, max: 16 },
        reason:
          'Spelfout vinden in zin vereist meta-cognitie over de regel — kerntaak voor zelfstandig schrijfwerk in groep 7-8.',
      },
      {
        type: 'drag_drop_fill',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 4, max: 16 },
        reason:
          'Letter/lettergreep-fragmenten in juiste positie slepen activeert woordbeeld actiever dan typen.',
      },
      {
        type: 'text_highlight',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 6, max: 16 },
        reason:
          'Specifieke woordvorm (b.v. samenstelling met tussen-n) markeren in zinscontext koppelt regel aan echte tekst.',
      },
      {
        type: 'fill_in',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 3, max: 16 },
        reason:
          'Ontbrekende letter/lettergreep invullen is directe regel-toepassing zonder afleidingseffect van distractors.',
      },
      {
        type: 'spelling_pick',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 3, max: 16 },
        reason:
          'Letterkeuze-cloze (ei/ij, au/ou, d/t): kind tikt de juiste grafeemtegel in een gat — productief (regel toepassen/onthouden) maar speels. Verbergt de spellingsbeslissing, dus geen verklap-effect zoals recall-MC. SPR-specialist productietype voor de recall-zware klank-/weet-families.',
      },
      {
        type: 'word_builder',
        didacticRole: 'concept',
        preferredDok: 2,
        appropriateTiers: { min: 5, max: 16 },
        reason:
          'Volledige-woord-producent (dictee-waardig): kind bouwt het HELE woord uit een geschudde letterbak, zónder opties en zónder grafeemkeuze — maximale productie, nul herkenning. De meest dictee-achtige stap voor recall-zware SPR-weet-families.',
      },
    ],
    familyTypeHints: [
      {
        familyIdPattern: '^SPR-KLANK-',
        preferredTypes: [
          'spelling_pick',
          'error_spotting',
          'drag_drop_fill',
          'categorize',
          'fill_in',
        ],
        discouragedTypes: ['true_false_series'],
        note: 'Klankregels (open/gesloten lettergreep, verdubbelen, verkorten) vereisen regel-toepassing op nieuw woord, geen herkenning. Homofoon-klankfamilies (au/ou) gebruiken spelling_pick zodat de letterkeuze verborgen blijft.',
      },
      {
        familyIdPattern: '^SPR-WEET-',
        preferredTypes: [
          'spelling_pick',
          'word_builder',
          'fill_in',
          'error_spotting',
        ],
        discouragedTypes: ['true_false_series', 'multiple_choice'],
        note: 'Weet-regels (d/t, ei/ij): de spelling moet GEPRODUCEERD worden (spelling_pick / word_builder / fill_in), niet herkend. multiple_choice is ontmoedigd — met volledig-gespelde opties verklapt het de spelling (zie CONTENT_SPR_WORD_VISIBLE).',
      },
      {
        familyIdPattern: '^SPR-(LEEN|SAM)-',
        preferredTypes: [
          'categorize',
          'matching',
          'fill_in',
          'multiple_choice',
        ],
        discouragedTypes: ['true_false_series'],
        note: 'Leenwoorden en samenstellingen: categoriseren naar bron-taal of meervoudspatroon — actieve classificatie.',
      },
      {
        familyIdPattern: '^SPR-HOOFDLETTER',
        preferredTypes: [
          'multiple_choice',
          'error_spotting',
          'sentence_error_judge',
          'fill_in',
          'text_highlight',
        ],
        discouragedTypes: ['true_false_series', 'matching', 'categorize'],
        note: 'Hoofdletters: CITO-norm vereist toepassing in zinscontext ("Welk woord moet een hoofdletter?", fout-spotten in een tekst, "Welke zin is goed?"). Abstracte regel-vragen (T-F/matching/categorize op losse woordlijsten) zijn verboden — ze toetsen herkenning i.p.v. toepassing.',
      },
      {
        familyIdPattern: '^SPR-(TREMA|ACCENT)',
        preferredTypes: [
          'spelling_pick',
          'fill_in',
          'error_spotting',
          'multiple_choice',
        ],
        note: 'Trema/accent: toepassing vóór herkenning — produceer de juiste vorm (spelling_pick/fill_in) of spot de fout in een woord/zin. categorize ("trema nodig / geen trema nodig") en matching ("woord → accenttype") zijn didactisch geldig als classificatie-stap op hoog tier en daarom NIET ontmoedigd; vermijd alleen true_false_series als enige vorm (recognition-only).',
      },
      {
        familyIdPattern: '^SPR-LEESTEKEN-',
        preferredTypes: [
          'error_spotting',
          'fill_in',
          'multiple_choice',
          'sentence_error_judge',
        ],
        discouragedTypes: ['true_false_series', 'matching'],
        note: 'Leestekens: CITO-norm vereist toepassing in context. Gebruik multiple_choice ("Welke zin is goed?"), error_spotting in korte teksten, of fill_in. Abstracte regel-vragen (matching/T-F) zijn verboden.',
      },
    ],
  },

  distractorTraps: [
    {
      id: 'syllable-misanalysis',
      label: 'Lettergreep verkeerd geanalyseerd',
      description:
        'Open lettergreep als gesloten lezen (of andersom) → verkeerde verdubbel/verkort-keuze.',
    },
    {
      id: 'homophone',
      label: 'Homofoon',
      description:
        'Woord met dezelfde klank maar andere spelling kiezen (b.v. wij/wei, ei/ij).',
    },
    {
      id: 'wrong-language-source',
      label: 'Verkeerde bron-taal toegepast',
      description:
        'Engelse spelling toepassen op Frans leenwoord (b.v. "cafe" i.p.v. "café").',
    },
    {
      id: 'tussen-letter-confused',
      label: 'Tussen-n / tussen-s verwisseld',
      description:
        'Tussen-s gebruiken waar tussen-n hoort (of andersom) — meervoudsregel niet toegepast.',
    },
  ],

  keyRules: [
    'Lettergreep-analyse altijd tonen in uitleg bij klankregels',
    'Open/gesloten lettergreep-onderscheid eerst benoemen voor verdubbel/verkort',
    'Bij leenwoorden: bron-taal expliciet noemen in uitleg',
    'Tussen-n: meervoud-uitsluitings-regel altijd tonen',
    'Productie boven herkenning: gebruik spelling_pick / word_builder / fill_in op recall-families (d/t, ei/ij, au/ou) i.p.v. multiple_choice — recall-MC met volledig-gespelde opties verklapt de spelling (CONTENT_SPR_WORD_VISIBLE)',
    'Zet de regel in een rule/support-aid, niet in de stam — een regel-opsomming in de vraag is onleesbaar en geeft stam/antwoord-mismatch (CONTENT_RULE_ENUMERATION_IN_STEM)',
    'true_false_series vermijden — past niet bij regel-toepassing',
  ],

  // GSR-23 (PR-9): SPR-distractors zijn spellingsvarianten (open/gesloten
  // lettergreep, leenwoorden, samenstellingen) — geen getallen. Numeric-distractor
  // parity check past niet bij regel-toepassing en wordt expliciet overgeslagen.
  qualityChecks: {
    skipNumericDistractorParity: true,
  },

  // GSR-25 (PR-9): per-module override copy migrated from inline
  // `moduleMessages` maps in didacticDepth.ts.
  qualityMessages: {
    explanationDepthRequirement:
      'SPR explanation must cite spelling rule (open/gesloten lettergreep, trema)',
    distractorMisconceptionRequirement:
      'SPR distractors should reference spelling rule error',
  },

  authoringGuideAnchor: '#6-spr--spelling-woordvorming',
}
